import { Component, ViewEncapsulation, ChangeDetectionStrategy, inject } from '@angular/core';
import { catchError, delay, forkJoin, from, Observable, of, takeUntil, takeWhile, tap } from 'rxjs';
import { MatTableDataSource } from '@angular/material/table';
import { ClientAuthService } from '../services/auth/client-auth.service';
import { ConfigurationState, OrderListItem, SelectOption, UserGroupAccessRepositoryService } from 'reg-hub-common';
import { OrderListRepositoryService } from '../services/order-list/order-list-repository.service';
import { TranslocoService } from '@jsverse/transloco';

@Component({
    selector: 'app-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DashboardComponent {
  private readonly transloco = inject(TranslocoService);
  private _userID: string = '';
  _userGroupID: string = '';
  _displayName: string = 'ALL';
  protected _userGroups: SelectOption[] = [];

  protected _loading: boolean = false;

  protected _draftOrders: OrderListItem[] = [];
  protected _totalDraftOrders: number = 0;

  protected _invalidOrders: OrderListItem[] = [];
  protected _totalInvalidOrders: number = 0;

  protected _pendingOrders: OrderListItem[] = [];
  protected _totalPendingOrders: number = 0;

  protected _completeOrders: OrderListItem[] = [];
  protected _totalCompleteOrders: number = 0;

  constructor(
    protected ordersRepo: OrderListRepositoryService,
    protected configurationState: ConfigurationState,
    protected userGroupAccess: UserGroupAccessRepositoryService) { }

  ngOnInit(): void {
    this._loading = true;
    
    // this.configurationState.getUserID().then(userID => {
    //   this._userID = userID;
    //   this._userGroupID = this.configurationState.getCurrentDashboardFilter();
    //   this._displayName = this.configurationState.getCurrentDashboardLabel();
    //   this.userGroupAccess.getReadableUserAccessesAsSelectOptions().subscribe(readAccess => {
    //     this._userGroups = readAccess;
    //     this.populateDashboard();
    //   })
    // });

    this._userGroupID = this.configurationState.getCurrentDashboardFilter();
    this._displayName = this.configurationState.getCurrentDashboardLabel();

    // Order panels don't depend on the group list, so load both in parallel
    this.populateDashboard();

    this.userGroupAccess
      .getReadableUserAccessesAsSelectOptions()
      .pipe(catchError(() => of([])))
      .subscribe(groups => this._userGroups = groups);
  }

  toggleVisiblity(groupID: any, displayName: string) {
    this._userGroupID = groupID;
    this._displayName = groupID === 'MyFiles' ? this.transloco.translate('dashboard.myFilesOnly') : displayName;
    this.configurationState.setCurrentDashboardFilter(groupID);
    this.configurationState.setCurrentDashboardLabel(displayName);
    this.populateDashboard();
  }

  populateDashboard() {
    this._loading = true;

    // userID is only needed for the "My Files" filter; getUserID() resolves from the shared configuration
    const userID$: Observable<string | undefined> = this._userGroupID === 'MyFiles'
      ? from(this.configurationState.getUserID()).pipe(tap(id => this._userID = id))
      : of(undefined);
    const userGroupID = this._userGroupID !== '' && this._userGroupID !== 'MyFiles' ? this._userGroupID : undefined;

    userID$.pipe(catchError(() => of(undefined))).subscribe(userID => this.loadOrders(userGroupID, userID));
  }

  private loadOrders(userGroupID: string | undefined, userID: string | undefined) {
    const draftOrdersRequest = this.ordersRepo.getTopTenDraftOrders(userGroupID, userID).pipe(catchError(() => of({ total: 0, resource: [] })));
    const invalidOrdersRequest = this.ordersRepo.getTopTenInvalidOrders(userGroupID, userID).pipe(catchError(() => of({ total: 0, resource: [] })));
    const pendingOrdersRequest = this.ordersRepo.getTopTenPendingAndExceptionOrders(userGroupID, userID).pipe(catchError(() => of({ total: 0, resource: [] })));
    const completeOrdersRequest = this.ordersRepo.getTopTenCompleteOrders(userGroupID, userID).pipe(catchError(() => of({ total: 0, resource: [] })));

    //Take the requests for all 4 panels, combine them into one observable, and only set loading to false when all are complete
    forkJoin({
      draftOrders: draftOrdersRequest,
      pendingOrders: pendingOrdersRequest,
      invalidOrders: invalidOrdersRequest,
      completeOrders: completeOrdersRequest
    }).subscribe({
      next: responses => {
        this._draftOrders = responses.draftOrders.resource;
        this._totalDraftOrders = responses.draftOrders.total;
        this._invalidOrders = responses.invalidOrders.resource;
        this._totalInvalidOrders = responses.invalidOrders.total;
        this._pendingOrders = responses.pendingOrders.resource;
        this._totalPendingOrders = responses.pendingOrders.total;
        this._completeOrders = responses.completeOrders.resource;
        this._totalCompleteOrders = responses.completeOrders.total;
      },
      complete: () => this._loading = false
    });
  }
}
